import React from 'react';
import { 
  FiSearch, 
  FiTrendingUp, 
  FiTarget, 
  FiCheckCircle,
  FiArrowRight,
  FiAward,
  FiBarChart,
  FiGlobe
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { Image } from '../../components/ui/Image';
import { imageConfig } from '../../config/images';

export const SEOPage: React.FC = () => {
  const seoServices = [
    {
      title: 'Technical SEO',
      description: 'Optimize your website infrastructure for maximum search engine crawlability',
      icon: <FiTarget className="text-4xl" />,
      features: [
        'Site Speed Optimization',
        'Mobile Responsiveness',
        'XML Sitemap Creation',
        'Robots.txt Optimization',
        'Schema Markup Implementation',
        'Core Web Vitals Enhancement'
      ],
      price: '₹25,000',
      duration: 'one-time audit + fixes'
    },
    {
      title: 'On-Page SEO',
      description: 'Optimize every element of your web pages for better rankings',
      icon: <FiSearch className="text-4xl" />,
      features: [
        'Keyword Research & Analysis',
        'Meta Tags Optimization',
        'Content Optimization',
        'Internal Linking Structure',
        'Image Alt Text Optimization',
        'URL Structure Improvement'
      ],
      price: '₹18,000',
      duration: '/month'
    },
    {
      title: 'Off-Page SEO',
      description: 'Build authority and credibility through strategic link building',
      icon: <FiGlobe className="text-4xl" />,
      features: [
        'High-Quality Backlink Building',
        'Guest Posting',
        'Directory Submissions',
        'Social Bookmarking',
        'Brand Mentions',
        'Competitor Backlink Analysis'
      ],
      price: '₹22,000',
      duration: '/month'
    },
    {
      title: 'Local SEO',
      description: 'Dominate local search results and attract nearby customers',
      icon: <FiTarget className="text-4xl" />,
      features: [
        'Google Business Profile Optimization',
        'Local Citations Building',
        'Review Management',
        'Local Link Building',
        'NAP Consistency',
        'Local Content Creation'
      ],
      price: '₹15,000',
      duration: '/month'
    },
    {
      title: 'E-commerce SEO',
      description: 'Specialized SEO for online stores to increase product visibility',
      icon: <FiTrendingUp className="text-4xl" />,
      features: [
        'Product Page Optimization',
        'Category Page SEO',
        'Rich Snippets Implementation',
        'Product Schema Markup',
        'Faceted Navigation Optimization',
        'Shopping Feed Optimization'
      ],
      price: '₹30,000',
      duration: '/month'
    },
    {
      title: 'Content SEO',
      description: 'Create and optimize content that ranks and converts',
      icon: <FiBarChart className="text-4xl" />,
      features: [
        'SEO Content Strategy',
        'Keyword-Optimized Blog Posts',
        'Content Gap Analysis',
        'Content Refresh & Updates',
        'Long-Form Content Creation',
        'Content Performance Tracking'
      ],
      price: '₹20,000',
      duration: '/month'
    }
  ];

  const seoPackages = [
    {
      name: 'Starter SEO',
      price: '₹15,000',
      period: '/month',
      description: 'Perfect for small businesses starting their SEO journey',
      features: [
        'Up to 10 keywords',
        'On-page optimization',
        'Monthly reporting',
        'Basic link building',
        'Google Analytics setup'
      ],
      popular: false
    },
    {
      name: 'Professional SEO',
      price: '₹35,000',
      period: '/month',
      description: 'Comprehensive SEO for growing businesses',
      features: [
        'Up to 30 keywords',
        'Technical + On-page + Off-page SEO',
        'Content creation (2 blogs/month)',
        'Advanced link building',
        'Bi-weekly reporting',
        'Competitor analysis'
      ],
      popular: true
    },
    {
      name: 'Enterprise SEO',
      price: 'Custom',
      period: 'pricing',
      description: 'Advanced SEO for large businesses and e-commerce',
      features: [
        'Unlimited keywords',
        'Full SEO management',
        'Content creation (8+ blogs/month)',
        'Premium link building',
        'Weekly reporting',
        'Dedicated SEO manager',
        'Technical audits'
      ],
      popular: false
    }
  ];

  const results = [
    {
      metric: '+285%',
      label: 'Average Traffic Increase',
      icon: <FiTrendingUp className="text-3xl" />
    },
    {
      metric: '+180%',
      label: 'Keyword Rankings Improved',
      icon: <FiSearch className="text-3xl" />
    },
    {
      metric: '+220%',
      label: 'Lead Generation Growth',
      icon: <FiTarget className="text-3xl" />
    },
    {
      metric: '95%',
      label: 'Client Success Rate',
      icon: <FiAward className="text-3xl" />
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0 opacity-10">
          <Image 
            src={imageConfig.services.seoBackground} 
            alt="SEO Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            Professional <span className="text-yellow-400">SEO Services</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Dominate search rankings and drive qualified organic traffic to your website with 
            data-driven SEO strategies that deliver measurable results.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="mb-3 flex justify-center text-yellow-400">
                  {result.icon}
                </div>
                <div className="text-3xl font-bold text-yellow-400 mb-2">{result.metric}</div>
                <div className="text-lg">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Process Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Data-Driven SEO That Delivers Results</h2>
              <p className="text-lg text-gray-600 mb-8">
                Our proven SEO methodology combines technical expertise, content optimization, 
                and strategic link building to dominate search rankings and drive qualified organic traffic.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FiSearch className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Comprehensive Keyword Research</h3>
                    <p className="text-gray-600">Identify high-value keywords that your customers are searching for</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FiBarChart className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Technical Optimization</h3>
                    <p className="text-gray-600">Fix technical issues that prevent search engines from crawling your site</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <FiTrendingUp className="text-blue-600 text-2xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-2">Continuous Improvement</h3>
                    <p className="text-gray-600">Monthly reporting and optimization to maximize your ROI</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl p-8 shadow-2xl">
                <Image 
                  src={imageConfig.hero.analyticsChart} 
                  alt="SEO Analytics Dashboard" 
                  className="rounded-lg shadow-lg"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">+285%</div>
                  <div className="text-sm text-gray-600">Traffic Increase</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our SEO Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive search engine optimization solutions tailored to your business goals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {seoServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 group">
                <div className="text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-700">
                      <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{service.price}</div>
                      <div className="text-sm text-gray-600">{service.duration}</div>
                    </div>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Get Started
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">SEO Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect SEO package for your business needs and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {seoPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  pkg.popular ? 'border-2 border-blue-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {pkg.price}
                    <span className="text-lg text-gray-600 font-normal">{pkg.period}</span>
                  </div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Mirani Marketing for SEO?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We blend industry expertise with cutting-edge technology to deliver measurable SEO results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiAward className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                Over 200+ successful SEO campaigns with an average 285% increase in organic traffic
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiBarChart className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Data-Driven Strategies</h3>
              <p className="text-gray-600 leading-relaxed">
                Every decision backed by analytics, competitor research, and performance data
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Transparent Reporting</h3>
              <p className="text-gray-600 leading-relaxed">
                Monthly reports showing keyword rankings, traffic growth, and ROI metrics
              </p>
            </div>
          </div>

          {/* Process Timeline */}
          <div className="bg-gray-50 rounded-3xl p-12 mt-16">
            <h3 className="text-4xl font-bold text-gray-800 mb-12 text-center">Our SEO Process</h3>
            <div className="space-y-8">
              <div className="flex items-start gap-6 group">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold group-hover:scale-110 transition-transform">
                  1
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">Initial Audit & Analysis</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Comprehensive website audit, competitor analysis, and keyword research to identify opportunities and create a customized strategy.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-6 group">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold group-hover:scale-110 transition-transform">
                  2
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">Technical Optimization</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Fix technical issues, improve site speed, implement schema markup, and ensure mobile-friendliness for better search engine crawling.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-6 group">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold group-hover:scale-110 transition-transform">
                  3
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">On-Page & Content Optimization</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Optimize existing content, create new SEO-optimized pages, and implement strategic internal linking structure.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-6 group">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold group-hover:scale-110 transition-transform">
                  4
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">Off-Page & Link Building</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Build high-quality backlinks through guest posting, digital PR, and strategic partnerships to increase domain authority.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-6 group">
                <div className="bg-blue-600 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold group-hover:scale-110 transition-transform">
                  5
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-gray-800 mb-2">Monitoring & Continuous Improvement</h4>
                  <p className="text-gray-600 leading-relaxed">
                    Track performance, analyze results, and continuously optimize strategies to maximize ROI and maintain competitive rankings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">SEO Services FAQs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get answers to common questions about our SEO services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How long does it take to see SEO results?",
                answer: "Typically, you'll start seeing initial improvements in 3-4 months, with significant results in 6-12 months. SEO is a long-term strategy that builds momentum over time. Early wins include improved technical health and on-page optimization, while rankings and traffic growth accelerate as authority builds."
              },
              {
                question: "What's included in your SEO services?",
                answer: "Our comprehensive SEO services include: technical SEO audit and fixes, keyword research and strategy, on-page optimization, content creation and optimization, link building, local SEO (if applicable), monthly reporting, and continuous optimization based on performance data."
              },
              {
                question: "How do you measure SEO success?",
                answer: "We track multiple metrics including: organic traffic growth, keyword rankings (page 1 positions), conversion rates from organic traffic, domain authority improvements, backlink quality and quantity, and most importantly - revenue generated from organic search."
              },
              {
                question: "Do you guarantee first page rankings?",
                answer: "While we can't guarantee specific rankings (no ethical SEO company can), we do guarantee measurable improvements in your SEO performance. Our data-driven approach has consistently delivered 285% average traffic increases for our clients."
              },
              {
                question: "What makes your SEO services different?",
                answer: "Unlike agencies that use cookie-cutter approaches, we create custom strategies based on your industry, competitors, and goals. We combine technical expertise with creative content strategies and focus on ROI, not just rankings. Plus, you get transparent monthly reporting showing exactly what we're doing and the results achieved."
              },
              {
                question: "Can you help with local SEO?",
                answer: "Absolutely! Our local SEO services include Google Business Profile optimization, local citation building, review management, location-specific content creation, and local link building to help you dominate local search results and attract nearby customers."
              },
              {
                question: "What's the minimum contract length?",
                answer: "We recommend a minimum 6-month commitment for SEO services. SEO takes time to show results, and this timeframe allows us to implement comprehensive strategies and deliver measurable improvements. Monthly plans are available with flexible terms."
              },
              {
                question: "Will I have a dedicated SEO manager?",
                answer: "Yes! Every client gets a dedicated SEO manager who understands your business, industry, and goals. You'll have direct access to your manager for questions, strategy discussions, and monthly performance reviews."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-blue-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-blue-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Dominate Search Rankings?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Get a free SEO audit and discover how we can increase your organic traffic 
            and generate more qualified leads for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-gray-800 px-10 py-5 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl"
            >
              Get Free SEO Audit
            </Link>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};